import { useRef } from 'react'
import moment from 'moment'
import { FormattedNumber } from 'i18n'

import IconButton from '@components/IconButton'
import CircleLoader from '@components/CircleLoader'
import Checkbox from '@components/Checkbox'

import { getDateTimeFormat } from 'i18n/utils'
import useClickOutside from 'shared/hooks/useClickOutside'
import { currencyFromSymbol } from 'shared/currency-utilities'
import TableCell from 'shared/components/TableCell'
const AddPromoCodeForm = ({ onClickOutside, onSubmit, value, label, onChange }) => {
    const formRef = useRef(null)

    useClickOutside(formRef, onClickOutside)

    return (
        <form ref={formRef} onSubmit={onSubmit}>
            <input type="text" className="form-control" value={value} autoFocus={true} onChange={onChange} />
            <button disabled={!value}>{label}</button>
        </form>
    )
}

const Item = props => {
    const [rawPromoCode, setRawPromoCode] = React.useState('')
    const [addingPromoCode, setAddingPromoCode] = React.useState(false)
    const [hideError, setHideError] = React.useState(false)

    const {
        item,
        removeFromBasket,
        setAutoRenew,
        removePromoCode,
        redeemPromoCode,
        promoCodePending,
        makePurchasePending,
        translate: t,
    } = props
    const { rate, vehicles, promoCode, autoRenew = false } = item
    const { locationName, rateName, amount, currencySymbol, fromDate, toDate, promptAutoRenew } = rate
    const promoCodeError = promoCodePending instanceof Error && promoCodePending.message

    const currency = currencyFromSymbol(currencySymbol)
    const formattedFromDate = moment(fromDate).format(getDateTimeFormat())
    const formattedToDate = moment(toDate).format(getDateTimeFormat())
    const oldPrice = amount
    const price = promoCode ? amount - promoCode.promoDiscount : amount

    const onAutoRenewChange = (event, preset = null) => {
        const value = event !== null ? event.target.checked : preset
        setAutoRenew(item, value)
    }

    const onClickAddPromoCode = () => {
        setHideError(true)
        setAddingPromoCode(!item.promoCode)
    }

    const onPromoCodeChange = event => {
        setRawPromoCode(event.target.value.toUpperCase())
    }

    const onPromoCodeBlur = () => {
        setRawPromoCode('')
        setAddingPromoCode(false)
    }

    const onSubmitPromoCode = event => {
        event.preventDefault()
        setHideError(false)
        redeemPromoCode(item, rawPromoCode)
    }

    const onClickRemovePromoCode = () => {
        removePromoCode(item)
        setRawPromoCode('')
        setAddingPromoCode(false)
    }

    const onClickRemove = () => {
        removeFromBasket(item)
    }

    React.useEffect(() => {
        if (promoCode && promoCode.barcode) {
            onAutoRenewChange(null, false)
        }
    }, [promoCode])

    return (
        <div className="table-row">
            <TableCell className="transaction" fieldName={t('basket.transaction.label')}>
                <span>{rateName}</span>
                <span>{locationName}</span>
            </TableCell>
            <TableCell className="vehicle" fieldName={t('basket.vehicle.label')}>
                {vehicles.map((vehicle, index) => (
                    <div key={index}>{`${vehicle.make} ${vehicle.model} ${vehicle.tag}`}</div>
                ))}
            </TableCell>
            <TableCell className="period">
                <TableCell className="start" fieldName={t('dashboard.label.range.s.date')}>
                    <span>
                        <T>label.parking.start</T>{' '}
                    </span>
                    <span>{formattedFromDate}</span>
                </TableCell>
                <TableCell className="end" fieldName={t('dashboard.label.range.e.date')}>
                    <span>
                        <T>label.parking.end</T>{' '}
                    </span>
                    <span>{formattedToDate}</span>
                </TableCell>
                {promptAutoRenew && (
                    <Checkbox
                        checked={!!autoRenew}
                        disabled={!!promoCode}
                        onChange={onAutoRenewChange}
                        className="m-0 mt-3">
                        <T>{promoCode ? `booking.monthly.auto-renew-disabled` : `basket.auto.renew.label`}</T>
                    </Checkbox>
                )}
            </TableCell>
            <TableCell className="promo-code">
                {promoCode ? (
                    <IconButton
                        className="promo-code__remove"
                        icon="minus-round"
                        label={promoCode.barcode && promoCode.barcode.toUpperCase()}
                        disabled={makePurchasePending === true}
                        onClick={onClickRemovePromoCode}
                    />
                ) : promoCodePending === true ? (
                    <CircleLoader size="small" />
                ) : addingPromoCode ? (
                    <div>
                        <AddPromoCodeForm
                            onSubmit={onSubmitPromoCode}
                            value={rawPromoCode}
                            onChange={onPromoCodeChange}
                            label={<T>basket.promo.code.add.btn.label</T>}
                            onClickOutside={onPromoCodeBlur}
                        />
                        {promoCodeError && !hideError && <span className="error">{promoCodeError}</span>}
                    </div>
                ) : (
                    <IconButton
                        icon="plus-round"
                        label={<T>basket.promo.code.label</T>}
                        disabled={makePurchasePending === true || rate.promoCode === false}
                        onClick={onClickAddPromoCode}
                    />
                )}
            </TableCell>
            <TableCell className="total" fieldName={t('basket.total.label')}>
                {promoCode && (
                    <span className="total__old-price">
                        <FormattedNumber value={oldPrice} style="currency" currency={currency} />
                    </span>
                )}
                <FormattedNumber value={price} style="currency" currency={currency} />
            </TableCell>
            <TableCell className="remove">
                <button onClick={onClickRemove} disabled={makePurchasePending === true} />
            </TableCell>
        </div>
    )
}

export default withTranslate(Item)
